import React, { useEffect, useRef } from 'react';
import TempPasswordReadout from './TempPasswordReadout';

/**
 * ⛔⛔ THE ONE-TIME PASSWORD, HELD BY THE PAGE — Card PORTAL-RESET-READOUT-d,
 * 6 Oct 2026.
 *
 * This panel exists because the password used to live inside the member row,
 * and the member row is DESTROYED whenever the org detail refetches.
 *
 * ── WHAT WAS MEASURED, IN DANIEL'S OWN BROWSER (23:30:50Z) ──────────────────
 *
 *   23:30:50.0  reset-portal-signin → 200 {success, partnerUserId, email,
 *               temporaryPassword}        ← the server DELIVERED the password
 *   23:30:50.0  orgs:get + accruals + submissions + audit   (refetch #1)
 *   23:30:50.8  the reset panel is GONE
 *   23:30:53.0  orgs:get + accruals + submissions           (refetch #2, +3s)
 *   23:30:53.825 the detail content disappears completely
 *   23:30:53.984 it reappears — a full UNMOUNT / REMOUNT, 160 ms apart
 *
 * No console error. Twelve IPC calls, all 200. The password was delivered and
 * then thrown away by a re-render, and Daniel pressed reset again because of
 * it (CloudTrail: AdminSetUserPassword OK at 11:13:44 AND 11:16:47 EDT).
 *
 * ── ⛔ WHY LIFTING IT IS THE REAL FIX, NOT JUST STOPPING THE REFETCH ────────
 *
 * The refetch triggers were removed too (see AlliancePartnersPage), but that
 * only disarms the two we FOUND. `onChanged`, a tab action, a future poll —
 * any of them legitimately refetches, and every one of them would have
 * destroyed a credential that exists nowhere else and cannot be re-issued
 * without burning another Cognito reset. A value this irreplaceable must not
 * be stored somewhere a re-render can reach.
 *
 * ⇒ The page owns it. The subtree below may mount and unmount freely.
 *
 * ── WHAT THIS PANEL DOES NOT DO ─────────────────────────────────────────────
 *
 * ⛔⛔ NO COPY BUTTON AND NO navigator.clipboard — Daniel's ruling P35 item 2,
 * 3 Oct 2026. The clipboard OUTLIVES this panel: it survives "Done", survives
 * the page, and is captured by every clipboard-history tool, which is exactly
 * what the runbook's "do not email it, text it, or paste it into a chat"
 * forbids. Staff READ IT ALOUD. A control arm asserts this file contains no
 * clipboard call.
 *
 * ⛔ IT IS NEVER LOGGED AND NEVER PERSISTED. It lives in one React state value
 * in the page and is dropped when the staff member presses Done — by them, not
 * by a timer and not by a refetch.
 */
export default function OneTimeCredentialPanel({ credential, onDone }) {
  const headingRef = useRef(null);

  /**
   * ⛔⛔ BRING IT INTO VIEW — Card PORTAL-RESET-READOUT-e.
   *
   * The panel is owned by the PAGE, which is what stops a refetch destroying
   * it — but the page renders it at the TOP, and the member row the staff
   * member just clicked is a full scroll BELOW. Nothing moved the eye: the
   * flash banner clears itself after 3 s, so what a staff member actually saw
   * was a message vanish and nothing appear.
   *
   * ⚠⚠ THAT IS THE SAME FAILURE IN A NEW COSTUME. On 6 Oct Daniel pressed
   * reset TWICE (CloudTrail 11:13:44 and 11:16:47) because he believed it had
   * not worked. Every extra press SILENTLY REPLACES the password, so a reset
   * the staff member cannot find is not a cosmetic problem — it burns the
   * credential it just issued.
   *
   * ⚠ HOOKS BEFORE THE EARLY RETURN. `if (!credential) return null` used to be
   * the first statement; a hook after it would run conditionally and break the
   * Rules of Hooks (there is a CI job for exactly this). The effect guards on
   * `credential` internally instead.
   *
   * ⚠ scrollIntoView is FEATURE-DETECTED. jsdom does not implement it, so an
   * unguarded call throws in every React test that renders this panel.
   */
  useEffect(() => {
    if (!credential) return;
    const el = headingRef.current;
    if (!el) return;
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    /**
     * ⛔ FOCUS, NOT JUST SCROLL. A scroll moves the sighted eye; it does
     * nothing for a screen reader or for anyone navigating by keyboard. Moving
     * focus to the heading announces the region and puts the next Tab inside
     * the panel, next to Done.
     */
    el.focus();
  }, [credential]);

  if (!credential) return null;

  const { email, password, auditGap, kind } = credential;
  const isReset = kind !== 'issued';
  // ⚠ The test ids are the ones the existing suites already query, so the
  // readout moving house does not quietly retire the assertions that guard it.
  const testId = isReset ? 'portal-reset-password' : 'issued-password';

  return (
    <div
      data-testid={isReset ? 'portal-reset-result' : 'issued-result'}
      role="region"
      aria-label="Temporary password, shown once"
      style={{
        marginBottom: 12, padding: 14,
        background: '#FFF8E1', border: '2px solid #F0B429', borderRadius: 8,
      }}
    >
      {/*
        ⚠ tabIndex={-1} makes the heading focusable programmatically WITHOUT
        putting it in the tab order — the effect above moves focus here so the
        region is announced and the next Tab lands on Done.
      */}
      <div
        ref={headingRef}
        tabIndex={-1}
        data-testid={isReset ? 'portal-reset-heading' : 'issued-heading'}
        style={{ fontSize: 13, fontWeight: 700, color: '#8A6100', marginBottom: 8, outline: 'none' }}
      >
        Temporary password for {email} — shown once
      </div>

      <TempPasswordReadout password={password} testId={testId} />

      <div style={{ marginTop: 8, fontSize: 11, color: '#8A6100' }}>
        There is no copy button on purpose — do not email, text or paste it anywhere.
        It cannot be shown again; if it is lost, run the reset again.
      </div>

      {/*
        ⛔⛔ THE RESET WORKED AND THE AUDIT ROW DID NOT. Shown BELOW the password,
        never instead of it: the password above is real and Cognito has already
        accepted it, so withholding it would strand the partner for a bookkeeping
        failure. This is the half that needs a human, so it says which half.
      */}
      {auditGap && (
        <div
          data-testid="portal-reset-audit-gap"
          role="alert"
          style={{ marginTop: 8, padding: 8, background: '#FFF5F5', border: '1px solid #F5C2C7', borderRadius: 5, fontSize: 11, color: '#842029' }}
        >
          ⚠ The password above is real and the reset worked — read it out as normal.
          But the CRM could not record this reset in the audit log. <strong>Tell Daniel</strong>,
          so the missing entry is known.
        </div>
      )}

      {/*
        ⛔ THE ONLY THING THAT CLEARS IT. Not a timer, not a refetch, not a
        re-render — the staff member, once they have actually read it out.
      */}
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          data-testid={isReset ? 'portal-reset-done' : 'issued-done'}
          onClick={onDone}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, background: '#8A6100', color: '#fff', border: '1px solid #8A6100', borderRadius: 5, cursor: 'pointer' }}
        >
          Done — I have read it to the partner
        </button>
      </div>
    </div>
  );
}
